/**
 * Comments UI state (F-50..F-55) — one store per open board view, owned by
 * BoardScreen (the top bar, the canvas layers, and the sidebar panel all
 * read it). Same pattern as the F-27 SelectionUiStore: producers publish
 * into the store, only the consuming components re-render.
 *
 * Everything here is LOCAL view state — comment mode, which popover is open,
 * the show-resolved toggle. The threads themselves live in the Yjs doc
 * (@kola/shared comment accessors) and are read reactively via the session's
 * `commentsVersion`.
 */
import type { Anchor } from "@kola/shared";
import { createStore, type StoreApi } from "zustand/vanilla";

import type { GridLayoutSnapshot } from "../board/GridTable.js";
import type { BoardPoint } from "./anchoring.js";

/** An open new-thread composer (F-50): anchor + where its pin preview sits. */
export interface CommentDraft {
  /** Anchor the thread will be created with (docs/04 §3, hit-tested). */
  anchor: Anchor;
  /** Board-space point of the draft pin / composer bubble. */
  position: BoardPoint;
}

export interface CommentsUiState {
  /** F-50 comment mode: cursor becomes a pin; click drops a composer. */
  mode: boolean;
  /** Open new-thread composer, or null. Mutually exclusive with a popover. */
  draft: CommentDraft | null;
  /** Thread whose popover is open, or null. */
  openThreadId: string | null;
  /** F-53: show resolved threads' pins (dimmed). Default off. */
  showResolved: boolean;
  /** F-55: the comments sidebar panel. */
  panelOpen: boolean;
  /**
   * The grid's latest layout pass (published by GridTable.onLayout) — the
   * cell-anchor resolution input. Null while the grid is not mounted, which
   * the C4 orphan detector treats as "unknown", never as "rows deleted".
   */
  gridLayout: GridLayoutSnapshot | null;

  setMode(mode: boolean): void;
  openDraft(draft: CommentDraft): void;
  closeDraft(): void;
  openThread(threadId: string): void;
  closeThread(): void;
  setShowResolved(show: boolean): void;
  setPanelOpen(open: boolean): void;
  setGridLayout(layout: GridLayoutSnapshot | null): void;
}

export type CommentsUiStore = StoreApi<CommentsUiState>;

export function createCommentsUiStore(): CommentsUiStore {
  return createStore<CommentsUiState>((set) => ({
    mode: false,
    draft: null,
    openThreadId: null,
    showResolved: false,
    panelOpen: false,
    gridLayout: null,

    // The three "one thing at a time" rules: entering comment mode closes any
    // open bubble; opening a composer or a thread popover exits comment mode
    // and closes the other bubble (Miro behavior — a single floating comment
    // surface at a time).
    setMode: (mode) => set(mode ? { mode, draft: null, openThreadId: null } : { mode }),
    openDraft: (draft) => set({ draft, mode: false, openThreadId: null }),
    closeDraft: () => set({ draft: null }),
    openThread: (openThreadId) => set({ openThreadId, mode: false, draft: null }),
    closeThread: () => set({ openThreadId: null }),
    setShowResolved: (showResolved) => set({ showResolved }),
    setPanelOpen: (panelOpen) => set({ panelOpen }),
    setGridLayout: (gridLayout) => set({ gridLayout }),
  }));
}
